import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import usePermission from '../../hooks/usePermission';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Eye, Trash2, Ruler, RefreshCw, X, ChevronRight, Loader2, Edit } from 'lucide-react';
import ProjectSelect from '../../components/ProjectSelect';
import CftReport from './CftReport';
import WoodTypesTab from './WoodTypesTab';
import WoodInwardTab from './WoodInwardTab';
import WoodStockReport from './WoodStockReport';
import WoodUsageDashboard from './WoodUsageDashboard';
import WoodStockDashboard from './WoodStockDashboard';
import WoodTransfersTab from './WoodTransfersTab';

// CFT page tabs (?tab=… keeps the tab on refresh / in links)
const VIEWS = [['sheets', 'Sheets'], ['dashboard', 'Stock Dashboard'], ['report', 'Status Report'], ['inward', 'Wood Inward'], ['transfers', 'Transfers'],
  ['stock', 'Detailed CFT Report'], ['usage', 'Usage Dashboard'], ['woods', 'Wood Types']];

const STATUS_BADGES = {
  draft: 'bg-amber-100 text-amber-700 border-amber-300',
  pending: 'bg-blue-100 text-blue-700 border-blue-300',
  approved: 'bg-green-100 text-green-700 border-green-300',
  submitted: 'bg-gray-100 text-gray-700 border-gray-300',
};
const STATUS_LABELS = { draft: 'Draft', pending: 'Waiting for Approval', approved: 'Approved', submitted: 'Submitted' };
const fmtCft = n => Number(n || 0).toFixed(4);

export default function CFTListPage() {
  const { canWrite, canDelete } = usePermission('cft');
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/worker') ? '/worker/cft' : '/admin/cft';

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const view = VIEWS.some(([k]) => k === searchParams.get('tab')) ? searchParams.get('tab') : 'sheets';
  const setView = k => setSearchParams(k === 'sheets' ? {} : { tab: k }, { replace: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/cft-sheets', { params: { status } });
      setRows(res.data.rows || []);
      const map = {}; (res.data.counts || []).forEach(c => map[c.status] = c.c);
      map.all = Object.values(map).reduce((a, b) => a + b, 0);
      setCounts(map);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    try { await api.delete(`/cft-sheets/${deleteTarget.id}`); toast.success('Deleted'); setDeleteTarget(null); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  const TABS = ['all', 'draft', 'pending', 'approved', 'submitted'].filter(t => t !== 'submitted' || counts.submitted);
  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Ruler className="w-6 h-6 text-blue-600" /> CFT — Solid Wood Issue Sheets
          </h1>
          <p className="text-sm text-gray-500">Demand vs Issue vs Return with 30% offcut and excess/shortage</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"><RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} /></button>
          {canWrite && <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"><Plus className="w-4 h-4" /> New Sheet</button>}
        </div>
      </div>

      <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
        <div className="flex gap-1 border-b border-gray-200 min-w-max">
          {VIEWS.map(([k, l]) => (
            <button key={k} onClick={() => setView(k)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${view === k ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{l}</button>
          ))}
        </div>
      </div>

      {view === 'dashboard' ? <WoodStockDashboard />
        : view === 'transfers' ? <WoodTransfersTab />
        : view === 'report' ? <CftReport />
        : view === 'inward' ? <WoodInwardTab />
        : view === 'stock' ? <WoodStockReport />
        : view === 'usage' ? <WoodUsageDashboard />
        : view === 'woods' ? <WoodTypesTab /> : <>
      <div className="flex flex-wrap gap-2">
        {TABS.map(t => (
          <button key={t} onClick={() => setStatus(t)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${status === t ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            {t === 'all' ? 'All' : STATUS_LABELS[t]} {counts[t] !== undefined ? `(${counts[t]})` : ''}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {loading ? <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>
          : rows.length === 0 ? <div className="p-10 text-center text-gray-400 text-sm">No CFT sheets found</div>
          : (
            <table className="w-full text-sm min-w-[820px]">
              <thead><tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <th className="px-4 py-3">Sheet No.</th><th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Product</th><th className="px-4 py-3">Wood</th>
                <th className="px-4 py-3 text-right">Demand</th><th className="px-4 py-3 text-right">Issue</th>
                <th className="px-4 py-3 text-right">Net Diff.</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(s => {
                  const nd = Number(s.net_difference || 0);
                  return (
                    <tr key={s.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`${basePath}/${s.id}`)}>
                      <td className="px-4 py-3 font-semibold text-blue-700">{s.sheet_no}</td>
                      <td className="px-4 py-3">{s.project_name ? <><div className="font-medium text-gray-800">{s.project_name}</div>{s.project_code && <div className="text-xs text-gray-400">{s.project_code}</div>}</> : (s.project_code || '—')}</td>
                      <td className="px-4 py-3 text-gray-600">{s.item_name || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{s.wood_type || '—'}</td>
                      <td className="px-4 py-3 text-right">{fmtCft(s.total_demand_cft)}</td>
                      <td className="px-4 py-3 text-right">{fmtCft(s.total_issue_cft)}</td>
                      <td className={`px-4 py-3 text-right font-bold ${nd > 0 ? 'text-green-700' : nd < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                        {fmtCft(nd)} {nd > 0 ? '↑' : nd < 0 ? '↓' : ''}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full border text-xs font-semibold whitespace-nowrap ${STATUS_BADGES[s.status] || STATUS_BADGES.draft}`}>{STATUS_LABELS[s.status] || s.status}</span>
                        {s.status === 'draft' && s.rejected_reason && <div className="text-[11px] text-red-600 mt-0.5">Rejected</div>}
                        {s.status === 'approved' && <div className="text-[11px] text-gray-500 mt-0.5">{Number(s.assigned_count) ? `${s.assigned_count} worker(s)` : 'Not assigned'}</div>}
                      </td>
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-end gap-1.5">
                          <button onClick={() => navigate(`${basePath}/${s.id}`)} title="View" className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600"><Eye className="w-4 h-4" /></button>
                          {canWrite && s.status === 'draft' && <button onClick={() => navigate(`${basePath}/${s.id}?edit=1`)} title="Edit" className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600"><Edit className="w-4 h-4" /></button>}
                          {canDelete && s.status === 'draft' && <button onClick={() => setDeleteTarget(s)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>}
                          <ChevronRight className="w-4 h-4 text-gray-300 self-center" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
      </div>

      </>}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={id => { setShowCreate(false); navigate(`${basePath}/${id}?edit=1`); }} />}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 w-full max-w-sm space-y-4">
            <h3 className="font-bold text-gray-800">Delete CFT Sheet?</h3>
            <p className="text-sm text-gray-600">Delete draft <strong>{deleteTarget.sheet_no}</strong> and all its rows?</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm rounded-lg border border-gray-200">Cancel</button>
              <button onClick={confirmDelete} className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-semibold">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ project_id: '', project_item_id: '', wood_type_id: '', remarks: '' });
  const [woods, setWoods] = useState([]);
  useEffect(() => { api.get('/wood-types', { params: { active: 1 } }).then(r => setWoods(r.data.rows || [])).catch(() => {}); }, []);
  const wood = woods.find(w => String(w.id) === String(f.wood_type_id));
  const set = p => setF(s => ({ ...s, ...p }));
  useEffect(() => { api.get('/projects').then(r => setProjects(Array.isArray(r.data) ? r.data : (r.data.rows || r.data.projects || []))).catch(() => {}); }, []);
  useEffect(() => {
    if (!f.project_id) { setItems([]); return; }
    api.get(`/projects/${f.project_id}`).then(r => setItems(r.data.items || r.data.project_items || [])).catch(() => setItems([]));
  }, [f.project_id]);
  const create = async () => {
    if (!f.project_id) return toast.error('Select a project');
    setSaving(true);
    try { const res = await api.post('/cft-sheets', f); toast.success(`Created ${res.data.sheet_no}`); onCreated(res.data.id); }
    catch (err) { toast.error(err.response?.data?.message || 'Create failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-5 w-full max-w-md space-y-3">
        <div className="flex items-center justify-between"><h3 className="font-bold text-gray-800">New CFT Sheet</h3><button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button></div>
        <div><label className="block text-xs font-semibold text-gray-500 mb-1">Project *</label>
          <ProjectSelect projects={projects} value={f.project_id} onChange={v => set({ project_id: v })}
              placeholder="— Select —" className="minp" /></div>
        <div><label className="block text-xs font-semibold text-gray-500 mb-1">Product / Item</label>
          <select className="minp" value={f.project_item_id} onChange={e => set({ project_item_id: e.target.value })}>
            <option value="">— Optional —</option>{items.map(it => <option key={it.id} value={it.id}>{it.item_name}{it.proto_code ? ` (${it.proto_code})` : ''}</option>)}
          </select></div>
        <div><label className="block text-xs font-semibold text-gray-500 mb-1">Wood Type</label>
          <select className="minp" value={f.wood_type_id} onChange={e => set({ wood_type_id: e.target.value })}>
            <option value="">— Select wood —</option>{woods.map(w => <option key={w.id} value={w.id}>{w.name}{w.sizes?.length ? ` (${w.sizes.length} sizes)` : ''}</option>)}
          </select>
          {wood?.sizes?.length > 0 && <p className="text-[11px] text-purple-700 mt-1">The Issue section will be filled with its {wood.sizes.length} standard size(s) — just enter the quantities.</p>}
          {!woods.length && <p className="text-[11px] text-gray-400 mt-1">Add wood types in the Wood Types tab.</p>}</div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-200">Cancel</button>
          <button onClick={create} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Create'}</button>
        </div>
      </div>
      <style>{`.minp{width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:8px;font-size:13px;} .minp:focus{outline:2px solid #bfdbfe;}`}</style>
    </div>
  );
}
